import { Outlet } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  CreditCard,
  HeartPulse,
  LayoutDashboard,
  MessageCircle,
  Pill,
  ScanHeart,
  ScanLine,
  ShieldCheck,
  Stethoscope,
  UserCog,
} from "lucide-react";
import { AppShell } from "./AppShell";
import { LogoutButton } from "./LogoutButton";
import { SidebarBrand, type NavGroup } from "./Sidebar";
import { Badge } from "@/components/ui/Badge";
import { useSession, isPatient } from "@/lib/session";

export function PatientShell() {
  const { data: session } = useSession();
  const patient = isPatient(session) ? session : null;

  // Grouped by intent rather than one flat list: what am I doing now, what is
  // my health record, what is my account.
  const groups: NavGroup[] = [
    {
      heading: "Layanan",
      items: [
        { to: "/patient/dashboard", label: "Beranda", icon: LayoutDashboard, end: true },
        { to: "/patient/health-check", label: "Cek Kesehatan AI", icon: ScanHeart },
        { to: "/patient/chat", label: "Tanya Mediora", icon: MessageCircle },
        { to: "/patient/mri", label: "MRI Assist", icon: ScanLine },
        { to: "/patient/reservasi", label: "Buat Reservasi", icon: CalendarDays },
        { to: "/patient/triage", label: "Triase Gejala", icon: Stethoscope },
      ],
    },
    {
      heading: "Rekam Kesehatan",
      items: [
        { to: "/patient/health-hub", label: "Health Hub", icon: HeartPulse },
        { to: "/patient/medications", label: "Pengingat Obat", icon: Pill },
        {
          to: "/patient/notifications",
          label: "Notifikasi",
          icon: Bell,
          badge: patient?.unread_notifications,
        },
      ],
    },
    {
      heading: "Akun",
      items: [
        { to: "/patient/profile", label: "Profil Medis", icon: UserCog },
        { to: "/patient/security", label: "Keamanan", icon: ShieldCheck },
        { to: "/patient/subscription", label: "Langganan", icon: CreditCard },
      ],
    },
  ];

  return (
    <AppShell
      groups={groups}
      brand={<SidebarBrand subtitle="Portal Pasien" />}
      sidebarFooter={
        <div className="flex items-center gap-2.5 px-1 py-1">
          <span
            aria-hidden
            className="bg-void-700 text-void-100 grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold"
          >
            {patient?.name?.charAt(0).toUpperCase() ?? "?"}
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-void-100 block truncate text-sm font-semibold">
              {patient?.name ?? "Memuat…"}
            </span>
            <span className="text-void-500 tabular block truncate text-2xs">
              {patient?.rm_number}
            </span>
          </span>
          <LogoutButton as="patient" />
        </div>
      }
      topbarActions={
        patient?.credits_remaining != null && (
          // Credits used to only surface as a 429 error mid-conversation.
          <Badge tone={patient.credits_remaining > 0 ? "accent" : "critical"}>
            {patient.credits_remaining} kredit
          </Badge>
        )
      }
    >
      <Outlet />
    </AppShell>
  );
}
